import { useState } from 'react';
import { ArrowLeft, Save, Play, CalendarClock, Clock, Star, Trash2, Check, Wallet, ListChecks } from 'lucide-react';
import { useAgentStudioNavigate } from '../../lib/routes';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { CostControlEditor } from '../../components/CostControlEditor';
import { useFlowStore } from '../../store/flowStore';
import { cn, formatBRL } from '../../lib/utils';
import { toast } from '../../store/uiStore';
import { FLOW_STATUS_LABELS } from '../../lib/catalog';
import { defaultCostControl } from '../../lib/factories';
import type { Flow } from '../../types';

const STATUSES: Flow['status'][] = ['draft', 'published', 'inactive'];

export function EditorTopBar({
  saving,
  dirty,
  running,
  hasRun,
  onSave,
  onRun,
  onShowRun,
  onDelete,
}: {
  saving: boolean;
  dirty: boolean;
  running: boolean;
  hasRun: boolean;
  onSave: () => void;
  onRun: () => void;
  onShowRun: () => void;
  onDelete: () => void;
}) {
  const navigate = useAgentStudioNavigate();
  const meta = useFlowStore((s) => s.meta);
  const updateMeta = useFlowStore((s) => s.updateMeta);
  const [editingName, setEditingName] = useState(false);
  const [costOpen, setCostOpen] = useState(false);

  if (!meta) return null;

  const cost = meta.costControl ?? defaultCostControl();

  return (
    <div className="flex items-center gap-3 border-b border-line bg-white px-5 py-3">
      <Button variant="ghost" size="icon" icon={ArrowLeft} onClick={() => navigate('/flows')} aria-label="Voltar" />

      {/* Todo o restante alinhado à direita, em uma única linha */}
      <div className="ml-auto flex min-w-0 items-center gap-2">
        {/* Pills de status */}
        <div className="flex items-center gap-0.5 rounded-xl border border-line bg-cloud p-0.5">
          {STATUSES.map((st) => {
            const active = meta.status === st;
            return (
              <button
                key={st}
                onClick={() => updateMeta({ status: st })}
                className={cn(
                  'rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? st === 'published'
                      ? 'bg-success text-white'
                      : st === 'draft'
                        ? 'bg-warning text-white'
                        : 'bg-slate text-white'
                    : 'text-slate hover:text-ink',
                )}
              >
                {FLOW_STATUS_LABELS[st]}
              </button>
            );
          })}
        </div>

        {/* Agendamento + relógio (mock) */}
        <button
          onClick={() => toast.info('Agendamento (mock) — disponível com o backend.')}
          className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs text-slate hover:bg-cloud"
        >
          <CalendarClock className="h-3.5 w-3.5" /> Agendar
        </button>
        <button
          onClick={() => setCostOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-cloud"
          title="Controle de custo do fluxo"
        >
          <Wallet className="h-3.5 w-3.5 text-success" />
          {cost.monthlyCapBRL > 0 ? `${formatBRL(cost.spentBRL)} / ${formatBRL(cost.monthlyCapBRL)}` : 'Custo'}
        </button>
        <span className="hidden items-center gap-1.5 text-xs text-slate xl:flex">
          <Clock className="h-3.5 w-3.5" /> sob demanda
        </span>

        <div className="mx-1 h-6 w-px bg-line" />

        {/* Nome do fluxo editável */}
        {editingName ? (
          <input
            autoFocus
            value={meta.name}
            onChange={(e) => updateMeta({ name: e.target.value })}
            onBlur={() => setEditingName(false)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
            className="h-9 w-48 rounded-lg border border-brand-orange px-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
          />
        ) : (
          <button onClick={() => setEditingName(true)} className="max-w-[200px] truncate text-sm font-semibold text-ink hover:text-brand-orange" title="Clique para renomear">
            {meta.name}
          </button>
        )}
        <button
          onClick={() => updateMeta({ favorite: !meta.favorite })}
          className={cn('rounded-lg p-1.5 hover:bg-cloud', meta.favorite ? 'text-amber' : 'text-slate')}
          aria-label="Favoritar"
        >
          <Star className={cn('h-4 w-4', meta.favorite && 'fill-amber')} />
        </button>
        <button onClick={onDelete} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Excluir fluxo">
          <Trash2 className="h-4 w-4" />
        </button>

        <div className="mx-1 h-6 w-px bg-line" />

        {hasRun && (
          <Button variant="ghost" icon={ListChecks} onClick={onShowRun} title="Ver o passo a passo da última execução">
            Execução
          </Button>
        )}
        <Button variant="secondary" icon={dirty ? Save : Check} loading={saving} onClick={onSave}>
          {dirty ? 'Salvar' : 'Salvo'}
        </Button>
        <Button variant="primary" icon={Play} onClick={onRun} disabled={running}>
          {running ? 'Executando…' : 'Executar'}
        </Button>
      </div>

      <Modal open={costOpen} onClose={() => setCostOpen(false)} title="Controle de custo do fluxo" width="max-w-lg">
        <CostControlEditor
          value={cost}
          onChange={(cc) => updateMeta({ costControl: cc })}
          entityId={meta.id}
        />
      </Modal>
    </div>
  );
}
